import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  HiOutlineCloudArrowUp, 
  HiOutlineSparkles, 
  HiOutlineLink, 
  HiOutlineRocketLaunch,
  HiArrowRight,
  HiCheck
} from 'react-icons/hi2';
import Section from '../components/ui/Section';
import Container from '../components/ui/Container';
import GradientText from '../components/ui/GradientText';
import GlowCard from '../components/ui/GlowCard';
import GoldButton from '../components/ui/GoldButton';
import Accordion from '../components/ui/Accordion';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

// WhatsApp configuration
const WHATSAPP_NUMBER = '50379009458'; // Format: country code + number without +
const WHATSAPP_MESSAGE = encodeURIComponent('Hola DigitalFlow, quiero mi Catálogo Digital Pro');
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

// JSON-LD Schema
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'DigitalFlow Agency',
  description: 'Catálogo Digital Pro - Tu catálogo digital listo en 7 días',
  url: 'https://digitalflow.agency/catalogo',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'San Salvador',
    addressCountry: 'SV'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    availableLanguage: 'Spanish'
  },
  areaServed: {
    '@type': 'Country',
    name: 'El Salvador'
  }
};

const Catalogo = () => {
  const { scrollY } = useScroll();
  const [isMobile, setIsMobile] = useState(false);
  
  // Parallax effect for hero background
  const heroY = useTransform(scrollY, [0, 500], [0, 200]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-50px' },
    transition: { duration: 0.6 }
  };

  // Hero Section
  const HeroSection = () => (
    <Section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Parallax background */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-dark-navy via-dark-navy to-gold/10"
        style={{ y: heroY }}
      />
      
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-gold/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Radial glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />

      <Container className="relative z-10 text-center">
        <motion.div {...fadeUp}>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-[1.15] pb-1">
            <GradientText shimmer className="block pb-10">
              Tu catálogo digital
            </GradientText>
            <span className="text-light-gray">listo en 7 días</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-light-gray/80 mb-12 max-w-3xl mx-auto">
            Mostrá tus productos, precios y recibí pedidos directo por WhatsApp.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <GoldButton
              href={WHATSAPP_URL}
              shimmer
              dataAnalytics="cta-hero-whatsapp"
            >
              Quiero mi catálogo
            </GoldButton>
            
            <GoldButton
              variant="outline"
              onClick={() => {
                document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
              }}
              dataAnalytics="cta-hero-ejemplos"
            >
              Ver ejemplos
            </GoldButton>
          </div>

          {/* Trust badges */}
          <motion.div
            className="flex flex-wrap justify-center gap-4 md:gap-8 text-sm md:text-base text-light-gray/60"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <span>✓ Entrega en 7 días</span>
            <span>✓ Hosting incluido</span>
            <span>✓ Soporte local</span>
            <span>✓ Crédito fiscal</span>
          </motion.div>
        </motion.div>
      </Container>
    </Section>
  );

  // Componente individual para cada paso (necesario para usar hooks correctamente)
  const StepItem = ({ step, index, scrollYProgress, isLeft }) => {
    const IconComponent = step.icon;
    
    // Ajustar los rangos de scroll para dar más espacio a cada paso
    // Cada paso ahora ocupa ~22% del scroll en lugar de 25%
    const stepStart = index * 0.22;
    const stepEnd = stepStart + 0.22;
    
    const opacity = useTransform(
      scrollYProgress,
      [stepStart, stepStart + 0.15, stepEnd - 0.05, stepEnd],
      [0.3, 1, 1, 1],
      { clamp: true }
    );
    
    const y = useTransform(
      scrollYProgress,
      [stepStart, stepStart + 0.15],
      [100, 0],
      { clamp: true }
    );

    const scale = useTransform(
      scrollYProgress,
      [stepStart, stepStart + 0.15],
      [0.8, 1],
      { clamp: true }
    );

    const contentOpacity = useTransform(
      scrollYProgress,
      [stepStart, stepStart + 0.12],
      [0, 1],
      { clamp: true }
    );
    
    const boxShadow = useTransform(
      scrollYProgress,
      [stepStart, stepStart + 0.15],
      [
        '0 0 0px rgba(212, 175, 55, 0)',
        '0 0 40px rgba(212, 175, 55, 0.4), 0 0 80px rgba(212, 175, 55, 0.2)'
      ]
    );

    return (
      <motion.div
        style={{ opacity, y, scale }}
        className={`relative flex items-center gap-8 md:gap-12 ${
          isLeft ? 'flex-col md:flex-row' : 'flex-col md:flex-row-reverse'
        }`}
      >
        {/* Contenido del paso */}
        <motion.div
          className={`flex-1 ${isLeft ? 'md:text-right' : 'md:text-left'}`}
          style={{ opacity: contentOpacity }}
        >
          <motion.div
            className="inline-flex items-center gap-2 mb-4"
            initial={{ x: isLeft ? -20 : 20, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: index * 0.2 }}
          >
            <span className="text-2xl font-bold text-gold">0{index + 1}</span>
            <div className="h-px w-12 bg-gradient-to-r from-gold to-transparent" />
          </motion.div>

          <motion.h3
            className="font-display text-3xl md:text-4xl font-bold mb-4 text-light-gray"
            initial={{ x: isLeft ? -30 : 30, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: index * 0.2 + 0.1 }}
          >
            {step.title}
          </motion.h3>

          <motion.p
            className="text-light-gray/70 text-lg leading-relaxed mb-6"
            initial={{ x: isLeft ? -30 : 30, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: index * 0.2 + 0.2 }}
          >
            {step.description}
          </motion.p>

          {/* Indicador de completado */}
          <motion.div
            className="inline-flex items-center gap-2 text-gold font-medium"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: index * 0.2 + 0.3, type: "spring" }}
          >
            <HiCheck className="w-5 h-5" />
            <span className="text-sm">Paso completado</span>
          </motion.div>
        </motion.div>

        {/* Icono premium en el centro */}
        <div className="relative flex-shrink-0">
          {/* Círculo con gradiente animado */}
          <motion.div
            className="relative w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(212, 175, 55, 0.05))`,
              boxShadow
            }}
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              delay: index * 0.2 + 0.15, 
              type: "spring",
              stiffness: 100
            }}
          >
            {/* Icono */}
            <motion.div
              className={`absolute inset-0 bg-gradient-to-br ${step.color} rounded-full opacity-20 blur-xl`}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.3, 0.2]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: index * 0.5
              }}
            />
            <IconComponent className="relative w-12 h-12 md:w-16 md:h-16 text-gold z-10" />
            
            {/* Punto de conexión en la línea */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="w-4 h-4 rounded-full bg-gold border-4 border-dark-navy shadow-lg"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.2 + 0.2, type: "spring" }}
              />
            </div>
          </motion.div>

          {/* Flecha de conexión (solo visible en desktop, excepto el último) */}
          {index < 3 && (
            <motion.div
              className="hidden md:block absolute top-full left-1/2 transform -translate-x-1/2 mt-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: index * 0.2 + 0.4 }}
            >
              <HiArrowRight className="w-6 h-6 text-gold/40 rotate-90" />
            </motion.div>
          )}
        </div>

        {/* Espacio en móvil para alternar */}
        {!isLeft && (
          <div className="md:hidden flex-1" />
        )}
      </motion.div>
    );
  };

  // Cómo funciona Section - Diseño dinámico con scroll progresivo
  const ComoFuncionaSection = () => {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
      target: sectionRef,
      offset: ["start end", "end start"]
    });

    const steps = [
      {
        icon: HiOutlineCloudArrowUp,
        title: 'Envíanos tus productos',
        description: 'Comparte con nosotros fotos de alta calidad y la lista de precios de tus productos. Mientras más organizado esté tu contenido, más rápido trabajamos.',
        color: 'from-blue-500 to-cyan-500'
      },
      {
        icon: HiOutlineSparkles,
        title: 'Diseñamos tu catálogo web',
        description: 'Creamos un catálogo digital moderno y profesional, optimizado para convertir visitantes en clientes. Diseño único para tu marca.',
        color: 'from-purple-500 to-pink-500'
      },
      {
        icon: HiOutlineLink,
        title: 'Conectamos WhatsApp + Facebook/Instagram Shop',
        description: 'Integramos botones directos de WhatsApp en cada producto y conectamos con tus redes sociales para ventas automatizadas.',
        color: 'from-green-500 to-emerald-500'
      },
      {
        icon: HiOutlineRocketLaunch,
        title: 'Empezás a vender',
        description: 'Tu catálogo está listo y funcionando. Comienza a recibir pedidos inmediatamente por WhatsApp y a través de tus redes sociales.',
        color: 'from-gold to-amber-500'
      }
    ];

    return (
      <Section id="como-funciona" variant="banded" ref={sectionRef}>
        <Container>
          <motion.div {...fadeUp} className="text-center mb-20">
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
              <GradientText>Cómo funciona</GradientText>
            </h2>
            <p className="text-light-gray/70 text-lg max-w-2xl mx-auto">
              4 pasos simples para transformar tu negocio con un catálogo digital profesional
            </p>
          </motion.div>

          {/* Timeline vertical con pasos revelados progresivamente */}
          <div className="relative max-w-4xl mx-auto pb-20">
            {/* Línea vertical de conexión */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold/30 via-gold/50 to-gold/30 transform md:-translate-x-1/2 hidden md:block" />
            
            {/* Pasos */}
            <div className="space-y-48 md:space-y-64 lg:space-y-80">
              {steps.map((step, index) => {
                const isLeft = index % 2 === 0;

                return (
                  <StepItem
                    key={index}
                    step={step}
                    index={index}
                    scrollYProgress={scrollYProgress}
                    isLeft={isLeft}
                  />
                );
              })}
            </div>

            {/* Call to action final */}
            <motion.div
              className="text-center mt-32 md:mt-40 lg:mt-48"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: 0.5 }}
            >
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <GoldButton
                  href={WHATSAPP_URL}
                  shimmer
                  className="text-lg px-8 py-4"
                  dataAnalytics="cta-como-funciona-whatsapp"
                >
                  ¡Quiero mi catálogo ahora! →
                </GoldButton>
              </motion.div>
            </motion.div>
          </div>
        </Container>
      </Section>
    );
  };

  // Qué incluye Section
  const QueIncluyeSection = () => {
    const features = [
      'Catálogo web moderno (SEO básico)',
      'Botón "Pedir por WhatsApp" por producto',
      'Integración con Facebook/Instagram Shop',
      'Dominio .com y hosting el primer mes',
      'Panel de métricas (GA4 / Looker Studio)',
      'Soporte y actualizaciones mensuales',
      'Bonus (opcional): Chatbot con IA para atención rápida'
    ];

    return (
      <Section id="que-incluye">
        <Container>
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-4">
              <GradientText>Qué incluye</GradientText>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <GlowCard key={index} delay={index * 0.05} className="flex items-start gap-3">
                <span className="text-gold text-xl flex-shrink-0">✓</span>
                <p className="text-light-gray/90">{feature}</p>
              </GlowCard>
            ))}
          </div>
        </Container>
      </Section>
    );
  };

  // Planes & Precios Section
  const PlanesSection = () => {
    const plans = [
      {
        name: 'Pro',
        price: '$399',
        period: 'setup',
        features: [
          'Entrega en 7 días',
          'Hasta 50 productos cargados',
          'WhatsApp integrado',
          'Conexión a Facebook/Instagram Shop',
          'Métricas básicas'
        ]
      },
      {
        name: 'Soporte Mensual',
        price: '$50',
        period: 'mes',
        features: [
          'Hosting + dominio (renovable)',
          'Soporte y pequeñas actualizaciones',
          'Monitoreo básico y seguridad'
        ]
      }
    ];

    return (
      <Section id="planes" variant="banded">
        <Container>
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-4">
              <GradientText>Planes & Precios</GradientText>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
            {plans.map((plan, index) => (
              <GlowCard key={index} delay={index * 0.1} className="relative">
                <div className="mb-6">
                  <h3 className="font-display text-2xl font-bold mb-2 text-light-gray">{plan.name}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gold">{plan.price}</span>
                    <span className="text-light-gray/60">/{plan.period}</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-light-gray/80">
                      <span className="text-gold">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </GlowCard>
            ))}
          </div>

          <motion.div {...fadeUp} className="text-center">
            <GoldButton
              href={WHATSAPP_URL}
              shimmer
              className="mb-4"
              dataAnalytics="cta-planes-whatsapp"
            >
              Empezar ahora →
            </GoldButton>
            <p className="text-light-gray/60 text-sm">¿Factura con crédito fiscal? Sí.</p>
          </motion.div>
        </Container>
      </Section>
    );
  };

  // Portfolio Section - Galería de Inspiración Visual
  const PortfolioSection = () => {
    // Estado para controlar el modal/lightbox
    const [selectedImage, setSelectedImage] = useState(null);

    // Configuración asimétrica de las 11 imágenes demo con layout creativo tipo masonry
    // Layout diseñado para crear un flujo visual dinámico e interesante
    const galleryItems = [
      // Fila 1: Imagen grande que domina, acompañada de pequeñas
      { src: '/assets/images/demo1.png', colSpan: 2, rowSpan: 2, rotation: -1.5, delay: 0 },
      { src: '/assets/images/demo2.png', colSpan: 1, rowSpan: 1, rotation: 1.2, delay: 0.1 },
      { src: '/assets/images/demo3.png', colSpan: 1, rowSpan: 1, rotation: -0.9, delay: 0.2 },
      // Fila 2: Continuación asimétrica
      { src: '/assets/images/demo4.png', colSpan: 2, rowSpan: 1, rotation: 1.4, delay: 0.3 },
      { src: '/assets/images/demo5.png', colSpan: 1, rowSpan: 1, rotation: -1.1, delay: 0.4 },
      // Fila 3: Imagen grande central con pequeñas a los lados
      { src: '/assets/images/demo6.png', colSpan: 1, rowSpan: 1, rotation: 0.8, delay: 0.5 },
      { src: '/assets/images/demo7.png', colSpan: 2, rowSpan: 2, rotation: -1.6, delay: 0.6 },
      { src: '/assets/images/demo8.png', colSpan: 1, rowSpan: 1, rotation: 1.0, delay: 0.7 },
      // Fila 4: Continúa el flujo asimétrico
      { src: '/assets/images/demo9.png', colSpan: 1, rowSpan: 1, rotation: -1.2, delay: 0.8 },
      { src: '/assets/images/demo10.png', colSpan: 2, rowSpan: 1, rotation: 0.6, delay: 0.9 },
      // Fila 5: Gran imagen final que cierra visualmente
      { src: '/assets/images/demo11.png', colSpan: 2, rowSpan: 2, rotation: 1.3, delay: 1.0 },
    ];

    // Cerrar modal con ESC
    useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          setSelectedImage(null);
        }
      };
      if (selectedImage) {
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden'; // Prevenir scroll del body
      }
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }, [selectedImage]);

    return (
      <Section id="portfolio">
        <Container>
          <motion.div {...fadeUp} className="text-center mb-20">
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
              <GradientText>Inspiración Visual</GradientText>
            </h2>
            <p className="text-light-gray/70 text-lg max-w-2xl mx-auto">
              Ejemplos de catálogos digitales que hemos creado. Cada diseño es único, 
              pensado para mostrar tus productos de la mejor manera y convertir visitantes en clientes.
            </p>
          </motion.div>

          {/* Galería asimétrica con layout creativo tipo masonry */}
          <div className="relative">
            <div 
              className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-6"
              style={{ gridAutoRows: 'minmax(200px, auto)' }}
            >
              {galleryItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.85, y: 40 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ 
                    duration: 0.7, 
                    delay: item.delay,
                    ease: [0.22, 1, 0.36, 1]
                  }}
                  className="group relative overflow-hidden rounded-xl md:rounded-2xl cursor-pointer"
                  style={{
                    gridColumn: `span ${item.colSpan}`,
                    gridRow: `span ${item.rowSpan}`,
                    minHeight: item.rowSpan === 2 ? '400px' : '250px'
                  }}
                >
                    {/* Contenedor con rotación sutil */}
                    <motion.div
                      className="relative w-full h-full"
                      style={{ 
                        rotate: item.rotation,
                        transformOrigin: 'center'
                      }}
                      whileHover={{ 
                        rotate: 0,
                        scale: 1.05,
                        transition: { duration: 0.4, ease: "easeOut" }
                      }}
                    >
                      {/* Imagen */}
                      <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-dark-navy to-dark-navy/80">
                        <motion.img
                          src={item.src}
                          alt={`Ejemplo de catálogo digital ${index + 1}`}
                          className="w-full h-full object-cover"
                          whileHover={{ 
                            scale: 1.15,
                            transition: { duration: 0.6, ease: "easeOut" }
                          }}
                          loading="lazy"
                        />
                        
                        {/* Overlay con gradiente dorado al hover */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-t from-gold/80 via-gold/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        />
                        
                        {/* Brillo sutil en esquina */}
                        <motion.div
                          className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold/30 to-transparent opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500"
                          animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0, 0.5, 0]
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            delay: index * 0.3
                          }}
                        />
                      </div>

                      {/* Borde brillante al hover */}
                      <motion.div
                        className="absolute inset-0 border-2 border-gold/0 group-hover:border-gold/60 rounded-xl md:rounded-2xl transition-all duration-500"
                        style={{
                          boxShadow: '0 0 0 0 rgba(212, 175, 55, 0)'
                        }}
                        whileHover={{
                          boxShadow: '0 0 30px rgba(212, 175, 55, 0.4), inset 0 0 30px rgba(212, 175, 55, 0.1)'
                        }}
                      />

                      {/* Efecto de partículas doradas al hover */}
                      {[...Array(6)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-2 h-2 bg-gold rounded-full opacity-0 group-hover:opacity-100"
                          style={{
                            left: `${20 + i * 15}%`,
                            top: `${30 + (i % 2) * 40}%`,
                          }}
                          initial={{ scale: 0, opacity: 0 }}
                          whileHover={{
                            scale: [0, 1.5, 0],
                            opacity: [0, 1, 0],
                            y: [-20, -40, -60],
                            x: [(i % 2 === 0 ? -10 : 10), (i % 2 === 0 ? -20 : 20), (i % 2 === 0 ? -30 : 30)],
                          }}
                          transition={{
                            duration: 1.5,
                            delay: i * 0.1,
                            repeat: Infinity,
                            repeatDelay: 2
                          }}
                        />
                      ))}

                      {/* Indicador de zoom sutil - clickeable */}
                      <motion.div
                        className="absolute bottom-4 right-4 w-10 h-10 bg-gold/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 shadow-lg cursor-pointer"
                        whileHover={{ scale: 1.15, rotate: 90 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(item.src);
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <svg className="w-5 h-5 text-dark-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM16 11l6 6" />
                        </svg>
                      </motion.div>
                    </motion.div>
                  </motion.div>
              ))}
            </div>

            {/* Decoración de fondo sutil */}
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-gold/5 rounded-full blur-3xl -z-10" />
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-gold/5 rounded-full blur-3xl -z-10" />
          </div>

          {/* Modal/Lightbox para imagen expandida */}
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              {/* Backdrop oscuro con blur */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-dark-navy/95 backdrop-blur-md"
                onClick={() => setSelectedImage(null)}
              />

              {/* Contenedor de imagen */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="relative z-10 max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Imagen expandida */}
                <motion.img
                  src={selectedImage}
                  alt="Imagen expandida"
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                />

                {/* Botón de cerrar */}
                <motion.button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 w-12 h-12 bg-gold/90 hover:bg-gold backdrop-blur-sm rounded-full flex items-center justify-center text-dark-navy font-bold text-xl shadow-lg z-20"
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  aria-label="Cerrar imagen"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>

                {/* Indicador de navegación - opcional para futuro */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-dark-navy/80 backdrop-blur-sm px-4 py-2 rounded-full text-gold text-sm">
                  Presiona ESC para cerrar
                </div>
              </motion.div>
            </motion.div>
          )}
        </Container>
      </Section>
    );
  };

  // FAQs Section
  const FAQsSection = () => {
    const faqs = [
      {
        question: '¿Cuánto tarda?',
        answer: '7 días desde que recibimos tu contenido. Trabajamos de forma ágil para tener tu catálogo listo rápidamente.'
      },
      {
        question: '¿Qué necesito enviar?',
        answer: 'Logo, colores, fotos y lista de productos con precios. Mientras más organizado esté tu contenido, más rápido avanzamos.'
      },
      {
        question: '¿Puedo pagar en partes?',
        answer: 'Sí, 50% al iniciar y 50% al entregar. Esto te da tranquilidad y nos permite comenzar de inmediato.'
      },
      {
        question: '¿Incluye SEO?',
        answer: 'Sí, optimización básica incluida: títulos, metadescripciones, OG tags y estructura semántica para que Google te encuentre.'
      },
      {
        question: '¿Puedo tener crédito fiscal?',
        answer: 'Sí, emitimos factura con IVA en El Salvador. Somos empresa formal y cumplimos con todos los requisitos fiscales.'
      },
      {
        question: '¿Qué pasa después del primer mes?',
        answer: 'Podés continuar con $50/mes para mantener hosting, soporte y actualizaciones, o migrar tu hosting a otro proveedor si lo preferís.'
      }
    ];

    return (
      <Section id="faqs" variant="banded">
        <Container maxWidth="max-w-4xl">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-6xl font-bold mb-4">
              <GradientText>Preguntas Frecuentes</GradientText>
            </h2>
          </motion.div>

          <Accordion items={faqs} />
        </Container>
      </Section>
    );
  };

  // Final CTA Section
  const FinalCTASection = () => (
    <Section id="cta-final" variant="diagonal">
      <Container>
        <motion.div {...fadeUp} className="text-center">
          <h2 className="font-display text-4xl md:text-6xl font-bold mb-6">
            <GradientText>Listo para vender más con un catálogo profesional?</GradientText>
          </h2>
          <p className="text-xl text-light-gray/80 mb-12">
            Escribinos y arrancamos esta semana.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <GoldButton
              href={WHATSAPP_URL}
              shimmer
              className="text-xl px-12 py-6"
              dataAnalytics="cta-final-whatsapp"
            >
              Contactar por WhatsApp
            </GoldButton>
            
            <GoldButton
              variant="outline"
              href="mailto:info@digitalflowsv.com?subject=Agendar llamada - Catálogo Digital Pro"
              className="text-xl px-12 py-6"
              dataAnalytics="cta-final-email"
            >
              Agendar llamada
            </GoldButton>
          </div>
        </motion.div>
      </Container>
    </Section>
  );

  return (
    <>
      <Helmet>
        <title>Catálogo Digital Pro - Tu catálogo digital listo en 7 días | DigitalFlow</title>
        <meta 
          name="description" 
          content="Mostrá tus productos, precios y recibí pedidos directo por WhatsApp. Catálogo digital profesional con integración completa. $399 setup + $50/mes." 
        />
        
        {/* Open Graph */}
        <meta property="og:title" content="Catálogo Digital Pro - DigitalFlow" />
        <meta property="og:description" content="Tu catálogo digital listo en 7 días. Integración con WhatsApp y redes sociales." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://digitalflow.agency/catalogo" />
        <meta property="og:image" content="https://digitalflow.agency/og-catalogo.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Catálogo Digital Pro - DigitalFlow" />
        <meta name="twitter:description" content="Tu catálogo digital listo en 7 días" />
        <meta name="twitter:image" content="https://digitalflow.agency/og-catalogo.png" />
        
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
        
        {/* TODO: OG Image Generation
          For OG image generation with Satori, create a serverless function:
          - Vercel: /api/og-catalogo.js using @vercel/og or satori
          - Or use a service like Cloudinary/ImageKit for dynamic OG images
          - Update og:image URL above to point to the generated image route
        */}
      </Helmet>

      <Navbar />
      
      <HeroSection />
      <ComoFuncionaSection />
      <QueIncluyeSection />
      <PortfolioSection />
      <PlanesSection />
      <FAQsSection />
      <FinalCTASection />
      <Footer />

      {/* Mobile Sticky CTA */}
      {isMobile && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 inset-x-0 z-50 p-4 bg-dark-navy/95 backdrop-blur-md border-t border-gold/20"
        >
          <GoldButton
            href={WHATSAPP_URL}
            className="w-full justify-center"
            dataAnalytics="cta-mobile-sticky"
          >
            Quiero mi catálogo → WhatsApp
          </GoldButton>
        </motion.div>
      )}

      {/* Add bottom padding on mobile to account for sticky CTA */}
      {isMobile && <div className="h-24" />}

      {/* Floating WhatsApp Button (Desktop) */}
      {!isMobile && (
        <motion.a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-8 right-8 z-50 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          data-analytics="cta-floating-whatsapp"
        >
          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
        </motion.a>
      )}

      {/* Analytics placeholder */}
      {/* TODO: Add GA4 and Meta Pixel tracking */}
    </>
  );
};

export default Catalogo;

